import React, { useState, useEffect, useRef } from 'react';

/**
 * LocationSearch Component
 * 
 * Provides autocomplete search for locations using a public API.
 * Returns standardized location data: { city, state, country, formatted }
 * 
 * Uses GeoNames API for location search (free tier available)
 * Fallback to a static list of major cities if API fails
 */

const LocationSearch = ({ 
  value, 
  onChange, 
  placeholder = "Search city...",
  className = "",
  disabled = false 
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Major US cities fallback (if API fails)
  const majorCities = [
    // === US — Major Metros & Financial Centers ===
    { city: 'New York', state: 'NY', country: 'United States' },
    { city: 'Los Angeles', state: 'CA', country: 'United States' },
    { city: 'Chicago', state: 'IL', country: 'United States' },
    { city: 'Houston', state: 'TX', country: 'United States' },
    { city: 'Phoenix', state: 'AZ', country: 'United States' },
    { city: 'Philadelphia', state: 'PA', country: 'United States' },
    { city: 'San Antonio', state: 'TX', country: 'United States' },
    { city: 'San Diego', state: 'CA', country: 'United States' },
    { city: 'Dallas', state: 'TX', country: 'United States' },
    { city: 'San Jose', state: 'CA', country: 'United States' },
    { city: 'Austin', state: 'TX', country: 'United States' },
    { city: 'Jacksonville', state: 'FL', country: 'United States' },
    { city: 'Fort Worth', state: 'TX', country: 'United States' },
    { city: 'Columbus', state: 'OH', country: 'United States' },
    { city: 'Charlotte', state: 'NC', country: 'United States' },
    { city: 'San Francisco', state: 'CA', country: 'United States' },
    { city: 'Indianapolis', state: 'IN', country: 'United States' },
    { city: 'Seattle', state: 'WA', country: 'United States' },
    { city: 'Denver', state: 'CO', country: 'United States' },
    { city: 'Washington', state: 'DC', country: 'United States' },
    { city: 'Boston', state: 'MA', country: 'United States' },
    { city: 'Nashville', state: 'TN', country: 'United States' },
    { city: 'Detroit', state: 'MI', country: 'United States' },
    { city: 'Portland', state: 'OR', country: 'United States' },
    { city: 'Las Vegas', state: 'NV', country: 'United States' },
    { city: 'Memphis', state: 'TN', country: 'United States' },
    { city: 'Louisville', state: 'KY', country: 'United States' },
    { city: 'Baltimore', state: 'MD', country: 'United States' },
    { city: 'Milwaukee', state: 'WI', country: 'United States' },
    { city: 'Albuquerque', state: 'NM', country: 'United States' },
    { city: 'Tucson', state: 'AZ', country: 'United States' },
    { city: 'Fresno', state: 'CA', country: 'United States' },
    { city: 'Sacramento', state: 'CA', country: 'United States' },
    { city: 'Atlanta', state: 'GA', country: 'United States' },
    { city: 'Miami', state: 'FL', country: 'United States' },
    { city: 'Tampa', state: 'FL', country: 'United States' },
    { city: 'Orlando', state: 'FL', country: 'United States' },
    { city: 'Raleigh', state: 'NC', country: 'United States' },
    { city: 'Minneapolis', state: 'MN', country: 'United States' },
    { city: 'Cleveland', state: 'OH', country: 'United States' },
    { city: 'Pittsburgh', state: 'PA', country: 'United States' },
    { city: 'St. Louis', state: 'MO', country: 'United States' },
    { city: 'Salt Lake City', state: 'UT', country: 'United States' },
    { city: 'San Juan', state: 'PR', country: 'United States' },
    // US — Additional Metros & State Capitals
    { city: 'Greenwich', state: 'CT', country: 'United States' },
    { city: 'Stamford', state: 'CT', country: 'United States' },
    { city: 'Hartford', state: 'CT', country: 'United States' },
    { city: 'New Haven', state: 'CT', country: 'United States' },
    { city: 'Westport', state: 'CT', country: 'United States' },
    { city: 'Darien', state: 'CT', country: 'United States' },
    { city: 'Jersey City', state: 'NJ', country: 'United States' },
    { city: 'Hoboken', state: 'NJ', country: 'United States' },
    { city: 'Princeton', state: 'NJ', country: 'United States' },
    { city: 'Newark', state: 'NJ', country: 'United States' },
    { city: 'Trenton', state: 'NJ', country: 'United States' },
    { city: 'White Plains', state: 'NY', country: 'United States' },
    { city: 'Albany', state: 'NY', country: 'United States' },
    { city: 'Buffalo', state: 'NY', country: 'United States' },
    { city: 'Rochester', state: 'NY', country: 'United States' },
    { city: 'Palm Beach', state: 'FL', country: 'United States' },
    { city: 'West Palm Beach', state: 'FL', country: 'United States' },
    { city: 'Fort Lauderdale', state: 'FL', country: 'United States' },
    { city: 'Boca Raton', state: 'FL', country: 'United States' },
    { city: 'Naples', state: 'FL', country: 'United States' },
    { city: 'Tallahassee', state: 'FL', country: 'United States' },
    { city: 'Palo Alto', state: 'CA', country: 'United States' },
    { city: 'Menlo Park', state: 'CA', country: 'United States' },
    { city: 'Santa Monica', state: 'CA', country: 'United States' },
    { city: 'Irvine', state: 'CA', country: 'United States' },
    { city: 'Newport Beach', state: 'CA', country: 'United States' },
    { city: 'Pasadena', state: 'CA', country: 'United States' },
    { city: 'Oakland', state: 'CA', country: 'United States' },
    { city: 'Honolulu', state: 'HI', country: 'United States' },
    { city: 'Anchorage', state: 'AK', country: 'United States' },
    { city: 'Richmond', state: 'VA', country: 'United States' },
    { city: 'Virginia Beach', state: 'VA', country: 'United States' },
    { city: 'Arlington', state: 'VA', country: 'United States' },
    { city: 'McLean', state: 'VA', country: 'United States' },
    { city: 'Bethesda', state: 'MD', country: 'United States' },
    { city: 'Annapolis', state: 'MD', country: 'United States' },
    { city: 'Wilmington', state: 'DE', country: 'United States' },
    { city: 'Dover', state: 'DE', country: 'United States' },
    { city: 'Providence', state: 'RI', country: 'United States' },
    { city: 'Burlington', state: 'VT', country: 'United States' },
    { city: 'Montpelier', state: 'VT', country: 'United States' },
    { city: 'Concord', state: 'NH', country: 'United States' },
    { city: 'Manchester', state: 'NH', country: 'United States' },
    { city: 'Portland', state: 'ME', country: 'United States' },
    { city: 'Augusta', state: 'ME', country: 'United States' },
    { city: 'Cambridge', state: 'MA', country: 'United States' },
    { city: 'Worcester', state: 'MA', country: 'United States' },
    { city: 'Durham', state: 'NC', country: 'United States' },
    { city: 'Charleston', state: 'SC', country: 'United States' },
    { city: 'Columbia', state: 'SC', country: 'United States' },
    { city: 'Savannah', state: 'GA', country: 'United States' },
    { city: 'Birmingham', state: 'AL', country: 'United States' },
    { city: 'Montgomery', state: 'AL', country: 'United States' },
    { city: 'Jackson', state: 'MS', country: 'United States' },
    { city: 'New Orleans', state: 'LA', country: 'United States' },
    { city: 'Baton Rouge', state: 'LA', country: 'United States' },
    { city: 'Little Rock', state: 'AR', country: 'United States' },
    { city: 'Oklahoma City', state: 'OK', country: 'United States' },
    { city: 'Tulsa', state: 'OK', country: 'United States' },
    { city: 'Kansas City', state: 'MO', country: 'United States' },
    { city: 'Omaha', state: 'NE', country: 'United States' },
    { city: 'Lincoln', state: 'NE', country: 'United States' },
    { city: 'Des Moines', state: 'IA', country: 'United States' },
    { city: 'Madison', state: 'WI', country: 'United States' },
    { city: 'St. Paul', state: 'MN', country: 'United States' },
    { city: 'Fargo', state: 'ND', country: 'United States' },
    { city: 'Bismarck', state: 'ND', country: 'United States' },
    { city: 'Sioux Falls', state: 'SD', country: 'United States' },
    { city: 'Boise', state: 'ID', country: 'United States' },
    { city: 'Helena', state: 'MT', country: 'United States' },
    { city: 'Cheyenne', state: 'WY', country: 'United States' },
    { city: 'Colorado Springs', state: 'CO', country: 'United States' },
    { city: 'Boulder', state: 'CO', country: 'United States' },
    { city: 'Santa Fe', state: 'NM', country: 'United States' },
    { city: 'Scottsdale', state: 'AZ', country: 'United States' },
    { city: 'Reno', state: 'NV', country: 'United States' },
    { city: 'Cincinnati', state: 'OH', country: 'United States' },
    { city: 'Lexington', state: 'KY', country: 'United States' },
    { city: 'Knoxville', state: 'TN', country: 'United States' },
    { city: 'Chattanooga', state: 'TN', country: 'United States' },
    // === Canada ===
    { city: 'Toronto', state: 'ON', country: 'Canada' },
    { city: 'Vancouver', state: 'BC', country: 'Canada' },
    { city: 'Montreal', state: 'QC', country: 'Canada' },
    { city: 'Calgary', state: 'AB', country: 'Canada' },
    { city: 'Ottawa', state: 'ON', country: 'Canada' },
    { city: 'Edmonton', state: 'AB', country: 'Canada' },
    { city: 'Winnipeg', state: 'MB', country: 'Canada' },
    { city: 'Quebec City', state: 'QC', country: 'Canada' },
    { city: 'Halifax', state: 'NS', country: 'Canada' },
    { city: 'Victoria', state: 'BC', country: 'Canada' },
    // === United Kingdom ===
    { city: 'London', state: '', country: 'United Kingdom' },
    { city: 'Edinburgh', state: '', country: 'United Kingdom' },
    { city: 'Manchester', state: '', country: 'United Kingdom' },
    { city: 'Birmingham', state: '', country: 'United Kingdom' },
    { city: 'Bristol', state: '', country: 'United Kingdom' },
    { city: 'Glasgow', state: '', country: 'United Kingdom' },
    { city: 'Leeds', state: '', country: 'United Kingdom' },
    { city: 'Liverpool', state: '', country: 'United Kingdom' },
    { city: 'Cambridge', state: '', country: 'United Kingdom' },
    { city: 'Oxford', state: '', country: 'United Kingdom' },
    { city: 'Cardiff', state: '', country: 'United Kingdom' },
    { city: 'Belfast', state: '', country: 'United Kingdom' },
    // === Europe ===
    { city: 'Paris', state: '', country: 'France' },
    { city: 'Lyon', state: '', country: 'France' },
    { city: 'Marseille', state: '', country: 'France' },
    { city: 'Berlin', state: '', country: 'Germany' },
    { city: 'Munich', state: '', country: 'Germany' },
    { city: 'Frankfurt', state: '', country: 'Germany' },
    { city: 'Hamburg', state: '', country: 'Germany' },
    { city: 'Düsseldorf', state: '', country: 'Germany' },
    { city: 'Stuttgart', state: '', country: 'Germany' },
    { city: 'Amsterdam', state: '', country: 'Netherlands' },
    { city: 'Rotterdam', state: '', country: 'Netherlands' },
    { city: 'The Hague', state: '', country: 'Netherlands' },
    { city: 'Dublin', state: '', country: 'Ireland' },
    { city: 'Cork', state: '', country: 'Ireland' },
    { city: 'Zurich', state: '', country: 'Switzerland' },
    { city: 'Geneva', state: '', country: 'Switzerland' },
    { city: 'Basel', state: '', country: 'Switzerland' },
    { city: 'Lugano', state: '', country: 'Switzerland' },
    { city: 'Luxembourg City', state: '', country: 'Luxembourg' },
    { city: 'Brussels', state: '', country: 'Belgium' },
    { city: 'Antwerp', state: '', country: 'Belgium' },
    { city: 'Vienna', state: '', country: 'Austria' },
    { city: 'Milan', state: '', country: 'Italy' },
    { city: 'Rome', state: '', country: 'Italy' },
    { city: 'Madrid', state: '', country: 'Spain' },
    { city: 'Barcelona', state: '', country: 'Spain' },
    { city: 'Lisbon', state: '', country: 'Portugal' },
    { city: 'Stockholm', state: '', country: 'Sweden' },
    { city: 'Copenhagen', state: '', country: 'Denmark' },
    { city: 'Oslo', state: '', country: 'Norway' },
    { city: 'Helsinki', state: '', country: 'Finland' },
    { city: 'Warsaw', state: '', country: 'Poland' },
    { city: 'Krakow', state: '', country: 'Poland' },
    { city: 'Prague', state: '', country: 'Czech Republic' },
    { city: 'Budapest', state: '', country: 'Hungary' },
    { city: 'Bucharest', state: '', country: 'Romania' },
    { city: 'Athens', state: '', country: 'Greece' },
    { city: 'Istanbul', state: '', country: 'Turkey' },
    { city: 'Monaco', state: '', country: 'Monaco' },
    // === Asia-Pacific ===
    { city: 'Singapore', state: '', country: 'Singapore' },
    { city: 'Hong Kong', state: '', country: 'Hong Kong' },
    { city: 'Tokyo', state: '', country: 'Japan' },
    { city: 'Osaka', state: '', country: 'Japan' },
    { city: 'Shanghai', state: '', country: 'China' },
    { city: 'Beijing', state: '', country: 'China' },
    { city: 'Shenzhen', state: '', country: 'China' },
    { city: 'Guangzhou', state: '', country: 'China' },
    { city: 'Hangzhou', state: '', country: 'China' },
    { city: 'Seoul', state: '', country: 'South Korea' },
    { city: 'Busan', state: '', country: 'South Korea' },
    { city: 'Taipei', state: '', country: 'Taiwan' },
    { city: 'Kuala Lumpur', state: '', country: 'Malaysia' },
    { city: 'Bangkok', state: '', country: 'Thailand' },
    { city: 'Jakarta', state: '', country: 'Indonesia' },
    { city: 'Manila', state: '', country: 'Philippines' },
    { city: 'Ho Chi Minh City', state: '', country: 'Vietnam' },
    { city: 'Hanoi', state: '', country: 'Vietnam' },
    { city: 'Bangalore', state: 'Karnataka', country: 'India' },
    { city: 'Mumbai', state: 'Maharashtra', country: 'India' },
    { city: 'New Delhi', state: 'Delhi', country: 'India' },
    { city: 'Hyderabad', state: 'Telangana', country: 'India' },
    { city: 'Chennai', state: 'Tamil Nadu', country: 'India' },
    { city: 'Pune', state: 'Maharashtra', country: 'India' },
    { city: 'Gurgaon', state: 'Haryana', country: 'India' },
    // === Australia & New Zealand ===
    { city: 'Sydney', state: 'NSW', country: 'Australia' },
    { city: 'Melbourne', state: 'VIC', country: 'Australia' },
    { city: 'Brisbane', state: 'QLD', country: 'Australia' },
    { city: 'Perth', state: 'WA', country: 'Australia' },
    { city: 'Adelaide', state: 'SA', country: 'Australia' },
    { city: 'Canberra', state: 'ACT', country: 'Australia' },
    { city: 'Auckland', state: '', country: 'New Zealand' },
    { city: 'Wellington', state: '', country: 'New Zealand' },
    // === Middle East & Africa ===
    { city: 'Dubai', state: '', country: 'United Arab Emirates' },
    { city: 'Abu Dhabi', state: '', country: 'United Arab Emirates' },
    { city: 'Riyadh', state: '', country: 'Saudi Arabia' },
    { city: 'Jeddah', state: '', country: 'Saudi Arabia' },
    { city: 'Doha', state: '', country: 'Qatar' },
    { city: 'Manama', state: '', country: 'Bahrain' },
    { city: 'Kuwait City', state: '', country: 'Kuwait' },
    { city: 'Muscat', state: '', country: 'Oman' },
    { city: 'Tel Aviv', state: '', country: 'Israel' },
    { city: 'Jerusalem', state: '', country: 'Israel' },
    { city: 'Johannesburg', state: '', country: 'South Africa' },
    { city: 'Cape Town', state: '', country: 'South Africa' },
    { city: 'Lagos', state: '', country: 'Nigeria' },
    { city: 'Nairobi', state: '', country: 'Kenya' },
    { city: 'Cairo', state: '', country: 'Egypt' },
    { city: 'Casablanca', state: '', country: 'Morocco' },
    // === Latin America & Caribbean ===
    { city: 'São Paulo', state: '', country: 'Brazil' },
    { city: 'Rio de Janeiro', state: '', country: 'Brazil' },
    { city: 'Mexico City', state: '', country: 'Mexico' },
    { city: 'Monterrey', state: '', country: 'Mexico' },
    { city: 'Buenos Aires', state: '', country: 'Argentina' },
    { city: 'Santiago', state: '', country: 'Chile' },
    { city: 'Bogotá', state: '', country: 'Colombia' },
    { city: 'Medellín', state: '', country: 'Colombia' },
    { city: 'Lima', state: '', country: 'Peru' },
    { city: 'Panama City', state: '', country: 'Panama' },
    { city: 'San José', state: '', country: 'Costa Rica' },
    { city: 'George Town', state: '', country: 'Cayman Islands' },
    { city: 'Nassau', state: '', country: 'Bahamas' },
    { city: 'Hamilton', state: '', country: 'Bermuda' },
  ];

  // Format location for display
  const formatLocation = (loc) => {
    if (!loc) return '';
    const parts = [loc.city];
    if (loc.state) parts.push(loc.state);
    if (loc.country && loc.country !== 'USA' && loc.country !== 'United States') parts.push(loc.country);
    return parts.join(', ');
  };

  // Search using API or fallback
  const searchLocations = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      // Try using a public geocoding API
      // Using Nominatim (OpenStreetMap) - free, no API key required
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&addressdetails=1&limit=10&featuretype=city`,
        { headers: { 'Accept-Language': 'en' } }
      );

      if (response.ok) {
        const data = await response.json();
        const locations = data
          .filter(item => item.address && (item.address.city || item.address.town || item.address.village))
          .map(item => ({
            city: item.address.city || item.address.town || item.address.village || item.address.municipality,
            state: item.address.state || item.address.county || '',
            country: item.address.country || '',
            formatted: item.display_name
          }))
          .filter((loc, index, self) => 
            // Remove duplicates
            index === self.findIndex(l => l.city === loc.city && l.state === loc.state && l.country === loc.country)
          );

        if (locations.length > 0) {
          setResults(locations);
          setIsSearching(false);
          return;
        }
      }
    } catch (err) {
      console.log('API search failed, using fallback:', err);
    }

    // Fallback to local search
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = majorCities.filter(loc => 
      loc.city.toLowerCase().includes(lowerQuery) ||
      loc.state.toLowerCase().includes(lowerQuery) ||
      loc.country.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);

    setResults(filtered);
    setIsSearching(false);
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchLocations(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  const handleSelect = (location) => {
    onChange(location);
    setQuery('');
    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setShowDropdown(true);
    setSelectedIndex(-1);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
  };

  return (
    <div className={`relative ${className}`}>
      {/* Display selected value or search input */}
      {value ? (
        <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="flex-1 text-sm text-gray-900">{formatLocation(value)}</span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-gray-400 hover:text-red-500"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {isSearching && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
      )}

      {/* Dropdown results */}
      {showDropdown && !value && results.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {results.map((loc, index) => (
            <button
              key={`${loc.city}-${loc.state}-${loc.country}-${index}`}
              type="button"
              onClick={() => handleSelect(loc)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
            >
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <div>
                <div className="font-medium text-gray-900">{loc.city}</div>
                <div className="text-xs text-gray-500">
                  {[loc.state, loc.country].filter(Boolean).join(', ')}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && !value && query.length >= 2 && results.length === 0 && !isSearching && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-sm text-gray-500">
          No locations found for "{query}"
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
